import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChevronDown, ChevronRight, Download, FileUp, Loader2, Plus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import MasterDataEntryForm from "../components/MasterDataEntryForm";

function groupProducts(products = []) {
  return Object.values(products.reduce((groups, product) => {
    const code = product.product_code;
    groups[code] ||= { ...product, serial_numbers: [] };
    groups[code].serial_numbers.push(...(product.serial_numbers || []));
    groups[code].serial_count = groups[code].serial_numbers.length;
    return groups;
  }, {}));
}

function formatDisplayDate(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
}

function ProductPanel({ product }) {
  return (
    <div className="rounded-md bg-background p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div><span className="font-heading font-bold tracking-tight">{product.product_code}</span>{product.description && <span className="ml-2 text-sm text-muted-foreground">{product.description}</span>}</div>
        <span className="text-xs text-muted-foreground">{product.serial_count} serial number</span>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground"><tr><th className="py-2 pr-4 font-medium">Serial Number</th><th className="py-2 font-medium">Tanggal Garansi</th></tr></thead>
        <tbody>
          {product.serial_numbers.map((serial) => <tr key={serial.id}><td className="py-2 pr-4 font-heading text-xs font-medium">{serial.serial_number}</td><td className="py-2 text-xs text-muted-foreground">{formatDisplayDate(serial.warranty_date)}</td></tr>)}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminMasterDataPage() {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const showSearch = searchParams.get("view") === "search";
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [serialResults, setSerialResults] = useState(undefined);
  const [expanded, setExpanded] = useState({});
  const [showSingle, setShowSingle] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [single, setSingle] = useState({ do_number: "", do_date: "", customer_name: "", sales: "", product_code: "", description: "", serial_number: "", warranty_date: "" });
  const [bulkText, setBulkText] = useState("");
  const [exportDoNumber, setExportDoNumber] = useState("");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-master-data", search],
    queryFn: () => api.get("/admin/master-data", { params: { search, page_size: 100 } }).then((response) => response.data),
  });

  const searchSerial = async (event) => {
    event.preventDefault();
    if (!serialSearch.trim()) return;
    try {
      const response = await api.get("/admin/master-data/search-serial", { params: { serial_number: serialSearch } });
      setSerialResults(response.data.items || (response.data.item ? [response.data.item] : []));
    } catch (error) {
      toast.error(apiError(error.response?.data));
    }
  };

  const removeOrder = async (order) => {
    if (!window.confirm(`Hapus DO ${order.do_number} beserta seluruh datanya?`)) return;
    try {
      await api.delete(`/admin/master-data/${order.id}`);
      toast.success("Delivery order berhasil dihapus");
      queryClient.invalidateQueries({ queryKey: ["admin-master-data"] });
    } catch (error) {
      toast.error(apiError(error.response?.data));
    }
  };

  const submitSingle = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/master-data", {
        do_number: single.do_number,
        do_date: single.do_date,
        customer_name: single.customer_name,
        sales: single.sales,
        products: [{ product_code: single.product_code, description: single.description, serial_numbers: [{ serial_number: single.serial_number, warranty_date: single.warranty_date || null }] }],
      });
      toast.success("DO dan SN berhasil ditambahkan");
      setSingle({ do_number: "", do_date: "", customer_name: "", sales: "", product_code: "", description: "", serial_number: "", warranty_date: "" });
      setShowSingle(false);
      queryClient.invalidateQueries({ queryKey: ["admin-master-data"] });
    } catch (error) { toast.error(apiError(error.response?.data)); } finally { setSaving(false); }
  };

  const submitBulk = async () => {
    const rows = parseCsv(bulkText);
    if (!rows.length || rows.some((row) => !row.do_number || !row.do_date || !row.product_code || !row.serial_number)) {
      toast.error("Format CSV: DO Number, Tanggal DO, Customer, Sales, Kode Product, Deskripsi, Serial Number, Tanggal Garansi");
      return;
    }
    setSaving(true);
    try {
      const response = await api.post("/admin/master-data/bulk", { rows });
      toast.success(response.data.message);
      setBulkText("");
      setShowBulk(false);
      queryClient.invalidateQueries({ queryKey: ["admin-master-data"] });
    } catch (error) { toast.error(apiError(error.response?.data)); } finally { setSaving(false); }
  };

  const chooseCsv = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const exportExcel = async () => {
    const doNumber = exportDoNumber.trim();
    if (!doNumber) return;
    setExporting(true);
    try {
      const response = await api.get("/admin/master-data/export", { params: { do_number: doNumber }, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `DO-${doNumber}.xls`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("File Excel berhasil diexport");
    } catch (error) {
      toast.error("Delivery Order tidak ditemukan atau gagal diexport.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4"><div><h1 className="font-heading text-4xl font-black tracking-tighter">Master Data</h1><p className="mt-1 text-muted-foreground">Delivery Order, product, serial number, dan tanggal garansi.</p></div><div className="flex gap-2"><Button variant="outline" onClick={() => setShowBulk(!showBulk)} className="gap-2"><Upload className="h-4 w-4" /> Bulk Import</Button><Button onClick={() => setShowSingle(!showSingle)} className="gap-2"><Plus className="h-4 w-4" /> Tambah DO/SN</Button></div></div>
      </div>

      {showSingle && <MasterDataEntryForm onSaved={() => { setShowSingle(false); queryClient.invalidateQueries({ queryKey: ["admin-master-data"] }); }} />}

      {showBulk && <div className="mb-6 rounded-lg border border-border bg-card p-5"><h2 className="mb-2 font-heading font-bold">Bulk Import CSV</h2><p className="mb-3 text-xs text-muted-foreground">Satu baris untuk satu SN. DO Number dan Kode Product yang sama akan dikelompokkan otomatis.</p><div className="mb-3 flex flex-wrap items-center gap-3"><a href="/master-data-template.csv" download className="text-sm text-primary hover:underline">Download Template CSV</a><label className="inline-flex cursor-pointer items-center gap-2 text-sm text-primary"><FileUp className="h-4 w-4" /> Pilih file CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={chooseCsv} /></label></div><textarea value={bulkText} onChange={(event) => setBulkText(event.target.value)} className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 font-sans text-xs" placeholder="DO-001,2026-09-01,PRD-01,Product A,SN-001,2027-09-01" /><Button type="button" onClick={submitBulk} disabled={saving} className="mt-3">{saving ? "Mengimpor..." : "Import Data"}</Button></div>}

      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"><div className="min-w-[240px] flex-1 space-y-2"><Label htmlFor="export-do-number">Export berdasarkan DO Number</Label><Input id="export-do-number" value={exportDoNumber} onChange={(event) => setExportDoNumber(event.target.value)} placeholder="Contoh: 72891892184" /></div><Button type="button" onClick={exportExcel} disabled={!exportDoNumber.trim() || exporting} className="gap-2"><Download className="h-4 w-4" />{exporting ? "Exporting..." : "Export Excel"}</Button></div>

      <div className={showSearch ? "grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start" : "w-full"}>
        {showSearch && <aside className="rounded-lg border border-border bg-card p-4 lg:sticky lg:top-20">
          <form onSubmit={searchSerial} className="space-y-3">
            <Label htmlFor="serial-search">Search Serial Number</Label>
            <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input id="serial-search" value={serialSearch} onChange={(event) => { setSerialSearch(event.target.value); setSerialResults(undefined); }} placeholder="Masukkan serial number" className="pl-9" /></div>
            <Button type="submit" className="w-full">Cari SN</Button>
          </form>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">Cari SN untuk melihat DO, product, deskripsi, dan tanggal garansi secara lengkap.</p>
        </aside>}

        <main>
          {showSearch && serialResults !== undefined ? <SerialSearchDetails results={serialResults} /> : <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isLoading ? <div className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div> : !data?.items?.length ? <div className="py-12 text-center text-sm text-muted-foreground">Belum ada master data.</div> : <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-xs text-muted-foreground"><tr><th className="w-10 px-4 py-3"></th><th className="px-4 py-3 font-medium">DO Number</th><th className="px-4 py-3 font-medium">Customer</th><th className="px-4 py-3 font-medium">Sales</th><th className="px-4 py-3 font-medium">Tanggal DO</th><th className="px-4 py-3 font-medium">Product</th><th className="px-4 py-3 font-medium">Serial Number</th><th className="w-12 px-4 py-3"></th></tr></thead>
          <tbody>
              {data.items.map((order) => {
              const products = groupProducts(order.products);
              const serialCount = products.reduce((total, product) => total + product.serial_count, 0);
              return <React.Fragment key={order.id}>
                <tr className="hover:bg-secondary/30">
                  <td className="px-4 py-3"><button type="button" onClick={() => setExpanded({ ...expanded, [order.id]: !expanded[order.id] })} className="rounded p-1 hover:bg-secondary" aria-label="Toggle detail">{expanded[order.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button></td>
                    <td className="px-4 py-3 font-semibold">{order.do_number}</td><td className="px-4 py-3">{order.customer_name}</td><td className="px-4 py-3">{order.sales || "-"}</td><td className="px-4 py-3 text-muted-foreground">{formatDisplayDate(order.do_date)}</td><td className="px-4 py-3">{products.length}</td><td className="px-4 py-3">{serialCount}</td><td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => removeOrder(order)} aria-label={`Hapus ${order.do_number}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                </tr>
                {expanded[order.id] && <tr><td colSpan="8" className="bg-secondary/20 px-4 py-3 sm:px-8"><div className="pl-6 sm:pl-10 lg:pl-14">{products.map((product) => <ProductPanel key={product.product_code} product={product} />)}</div></td></tr>}
              </React.Fragment>;
            })}
          </tbody>
        </table>}
          </div>}
        </main>
      </div>
    </Layout>
  );
}

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  const firstValues = lines[0]?.split(",").map((value) => value.trim().toLowerCase()) || [];
  const dataLines = firstValues[0] === "do number" || firstValues[0] === "do_number" ? lines.slice(1) : lines;
  return dataLines.map((line) => {
    const values = line.split(",").map((value) => value.trim().replace(/^"|"$/g, ""));
    return { do_number: values[0], do_date: values[1], customer_name: values[2], sales: values[3], product_code: values[4], description: values[5], serial_number: values[6], warranty_date: values[7] || null };
  });
}

function SerialSearchDetails({ results }) {
  results = results.map((result) => ({ ...result, do_date: formatDisplayDate(result.do_date), warranty_date: formatDisplayDate(result.warranty_date) }));
  return <div className="overflow-hidden rounded-lg border border-border bg-card">
    <div className="border-b border-border px-5 py-4"><h2 className="font-heading text-lg font-bold">Detail Serial Number</h2><p className="mt-1 text-xs text-muted-foreground">{results.length} hasil ditemukan</p></div>
    {!results.length ? <div className="py-12 text-center text-sm text-muted-foreground">Serial number belum ditemukan.</div> : <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-secondary/50 text-left text-xs text-muted-foreground"><tr><th className="px-5 py-3 font-medium">Serial Number</th><th className="px-5 py-3 font-medium">DO Number</th><th className="px-5 py-3 font-medium">Customer</th><th className="px-5 py-3 font-medium">Sales</th><th className="px-5 py-3 font-medium">Tanggal DO</th><th className="px-5 py-3 font-medium">Kode Product</th><th className="px-5 py-3 font-medium">Deskripsi</th><th className="px-5 py-3 font-medium">Tanggal Garansi</th></tr></thead><tbody>{results.map((result) => <tr key={result.id}><td className="px-5 py-3 font-heading font-medium">{result.serial_number}</td><td className="px-5 py-3">{result.do_number}</td><td className="px-5 py-3">{result.customer_name || "-"}</td><td className="px-5 py-3">{result.sales || "-"}</td><td className="px-5 py-3 text-muted-foreground">{result.do_date || "-"}</td><td className="px-5 py-3">{result.product_code || "-"}</td><td className="px-5 py-3 text-muted-foreground">{result.description || "-"}</td><td className="px-5 py-3 text-muted-foreground">{result.warranty_date || "-"}</td></tr>)}</tbody></table></div>}
  </div>;
}